import React, { useState, useMemo } from "react";
import {
  Header,
  Container,
  SpaceBetween,
  Box,
  Grid,
  Table,
  Pagination,
  TextFilter,
  Spinner,
  Select,
  SelectProps,
} from "@cloudscape-design/components";
import Plot from "react-plotly.js";
import { COLORS, TimeGranularity } from "./detailHelpers";
import { PAGE_SIZE_OPTIONS } from "../constants";
import type { ProgramDetailData, RequestStatus, DetailMachineDowntimeRow, DetailMachineDowntimeTrend, DetailMachineDowntimeTrendDataPoint } from "../../../redux/types/programTypes";

export interface DowntimeRow {
  program: string;
  parent_program: string | null;
  cm: string;
  cm_location: string;
  cmLocation: string;
  smtTarget: number | null;
  smtActual: number | null;
  fatpTarget: number | null;
  fatpActual: number | null;
  hasSmt: boolean;
  hasFatp: boolean;
}

const TIME_LABEL: Record<TimeGranularity, string> = { day: "Day", week: "Week", month: "Month" };

export const MachineDowntimeDetail: React.FC<{ detailData?: ProgramDetailData | null; detailStatus?: RequestStatus; timeGranularity?: TimeGranularity }> = ({ detailData = null, detailStatus = "idle", timeGranularity = "week" }) => {

  const [dtPage, setDtPage] = useState(1);
  const [dtSearchText, setDtSearchText] = useState("");
  const [expandedDtItems, setExpandedDtItems] = useState<ReadonlyArray<DowntimeRow>>([]);
  const [selectedPageSize, setSelectedPageSize] = useState<SelectProps.Option>(PAGE_SIZE_OPTIONS[0] as SelectProps.Option);
  const DT_PAGE_SIZE = parseInt(selectedPageSize.value || "10", 10);

  // Transform API rows: group by program + cm + cm_location, merge SMT/FATP into one row
  const tableData = useMemo(() => {
    if (!detailData?.table) return [];
    const apiRows = detailData.table as DetailMachineDowntimeRow[];
    const grouped: Record<string, DowntimeRow> = {};
    apiRows.forEach((row) => {
      const key = `${row.program}__${row.cm}__${row.cm_location}`;
      if (!grouped[key]) {
        grouped[key] = {
          program: row.program,
          parent_program: row.parent_program,
          cm: row.cm,
          cm_location: row.cm_location,
          cmLocation: `${row.cm}-${row.cm_location}`,
          smtTarget: null,
          smtActual: null,
          fatpTarget: null,
          fatpActual: null,
          hasSmt: false,
          hasFatp: false,
        };
      }
      if (row.stage === "SMT") {
        grouped[key].hasSmt = true;
        grouped[key].smtTarget = row.target;
        grouped[key].smtActual = row.machine_downtime;
      } else if (row.stage === "Testing" || row.stage === "FATP") {
        grouped[key].hasFatp = true;
        grouped[key].fatpTarget = row.target;
        grouped[key].fatpActual = row.machine_downtime;
      }
    });
    return Object.values(grouped);
  }, [detailData]);

  // Build parent-child mapping using parent_program + cm + cm_location
  const { dtDisplayItems, dtChildrenMap } = useMemo(() => {
    const childrenMap = new Map<string, DowntimeRow[]>();
    // Track existing parent keys (program + cm + cm_location for rows with parent_program = null)
    const existingParentKeys = new Set<string>();

    // Identify existing parents
    tableData.forEach((row) => {
      if (!row.parent_program) {
        existingParentKeys.add(`${row.program}__${row.cm}__${row.cm_location}`);
      }
    });

    // Gather children grouped by parent key
    tableData.forEach((row) => {
      if (row.parent_program) {
        const parentKey = `${row.parent_program}__${row.cm}__${row.cm_location}`;
        const existing = childrenMap.get(parentKey) || [];
        existing.push(row);
        childrenMap.set(parentKey, existing);
      }
    });

    // Build display items (top-level rows)
    const displayItems: DowntimeRow[] = [];
    // First add existing parent rows
    tableData.forEach((row) => {
      if (!row.parent_program) {
        displayItems.push(row);
      }
    });

    // Create virtual parent rows for orphan children (parent_program not found in data)
    const createdVirtualParents = new Set<string>();
    tableData.forEach((row) => {
      if (row.parent_program) {
        const parentKey = `${row.parent_program}__${row.cm}__${row.cm_location}`;
        if (!existingParentKeys.has(parentKey) && !createdVirtualParents.has(parentKey)) {
          createdVirtualParents.add(parentKey);
          // Create a virtual parent row with the parent_program name
          const virtualParent: DowntimeRow = {
            program: row.parent_program,
            parent_program: null,
            cm: row.cm,
            cm_location: row.cm_location,
            cmLocation: `${row.cm}-${row.cm_location}`,
            smtTarget: null,
            smtActual: null,
            fatpTarget: null,
            fatpActual: null,
            hasSmt: false,
            hasFatp: false,
          };
          displayItems.push(virtualParent);
        }
      }
    });

    return { dtDisplayItems: displayItems, dtChildrenMap: childrenMap };
  }, [tableData]);

  // Filtered display data
  const filteredDtDisplay = useMemo(() => {
    if (!dtSearchText.trim()) return dtDisplayItems;
    const q = dtSearchText.toLowerCase();
    return dtDisplayItems.filter((r) => {
      const rowKey = `${r.program}__${r.cm}__${r.cm_location}`;
      return (
        r.program.toLowerCase().includes(q) ||
        r.cmLocation.toLowerCase().includes(q) ||
        // Include parent if any children match
        (dtChildrenMap.get(rowKey) || []).some((child) =>
          child.program.toLowerCase().includes(q) ||
          child.cmLocation.toLowerCase().includes(q)
        )
      );
    });
  }, [dtDisplayItems, dtChildrenMap, dtSearchText]);

  const pageItems = filteredDtDisplay.slice((dtPage - 1) * DT_PAGE_SIZE, dtPage * DT_PAGE_SIZE);

  const apiTrend = detailData?.trend as DetailMachineDowntimeTrend[] | undefined;

  // Memoize chart data so expanding table rows doesn't cause chart re-renders
  const smtChartData = useMemo(() => {
    if (!Array.isArray(apiTrend)) return [];
    const traces: any[] = [];
    let colorIdx = 0;
    const smtItems = apiTrend.filter((item) => item.stage === "SMT");
    smtItems.forEach((item) => {
      const xAxis = (item.data || []).map((d: DetailMachineDowntimeTrendDataPoint) => d.period);
      const y = (item.data || []).map((d: DetailMachineDowntimeTrendDataPoint) => d.machine_downtime != null ? d.machine_downtime * 100 : null);
      traces.push({
        x: xAxis,
        y,
        name: `${item.program} (${item.cm}-${item.cm_location})`,
        type: "scatter" as const,
        mode: "lines+markers" as const,
        connectgaps: false,
        marker: { color: COLORS[colorIdx % COLORS.length], size: 5 },
        line: { width: 2, color: COLORS[colorIdx % COLORS.length] },
        hovertemplate: `<b>%{fullData.name}</b><br>Period: %{x}<br>Downtime: %{y:.2f}%<br>Target: ${item.target != null ? (item.target * 100).toFixed(2) : "N/A"}%<extra></extra>`,
      });
      colorIdx++;
    });
    return traces;
  }, [apiTrend]);

  const fatpChartData = useMemo(() => {
    if (!Array.isArray(apiTrend)) return [];
    const traces: any[] = [];
    let colorIdx = 0;
    const fatpItems = apiTrend.filter((item) => item.stage === "Testing" || item.stage === "FATP");
    fatpItems.forEach((item) => {
      const xAxis = (item.data || []).map((d: DetailMachineDowntimeTrendDataPoint) => d.period);
      const y = (item.data || []).map((d: DetailMachineDowntimeTrendDataPoint) => d.machine_downtime != null ? d.machine_downtime * 100 : null);
      traces.push({
        x: xAxis,
        y,
        name: `${item.program} (${item.cm}-${item.cm_location})`,
        type: "scatter" as const,
        mode: "lines+markers" as const,
        connectgaps: false,
        marker: { color: COLORS[colorIdx % COLORS.length], size: 5 },
        line: { width: 2, color: COLORS[colorIdx % COLORS.length] },
        hovertemplate: `<b>%{fullData.name}</b><br>Period: %{x}<br>Downtime: %{y:.2f}%<br>Target: ${item.target != null ? (item.target * 100).toFixed(2) : "N/A"}%<extra></extra>`,
      });
      colorIdx++;
    });
    return traces;
  }, [apiTrend]);

  const chartLayout = useMemo(() => ({
    height: 400, margin: { t: 10, b: 80, l: 50, r: 10 },
    yaxis: { rangemode: "tozero" as const, title: "Downtime %", ticksuffix: "%", domain: [0.35, 1] },
    xaxis: { title: "Period", tickangle: -45, type: "category" as const, automargin: true },
    legend: { orientation: "h" as const, y: 0, yanchor: "top" as const, xanchor: "center" as const, x: 0.5, font: { size: 9 } },
    font: { size: 10 },
  }), []);

  const chartConfig = useMemo(() => ({ displayModeBar: false, responsive: true }), []);

  if (detailStatus === "loading") {
    return (
      <Container header={<Header variant="h2">{`Machine Downtime — by ${TIME_LABEL[timeGranularity]}`}</Header>}>
        <Box textAlign="center" padding={{ vertical: "xl" }}>
          <SpaceBetween size="s" alignItems="center">
            <Spinner size="large" />
            <Box color="text-body-secondary">Loading detail data...</Box>
          </SpaceBetween>
        </Box>
      </Container>
    );
  }

  return (
    <div data-testid="program-detail-downtime">
    <Container header={<Header variant="h2">{`Machine Downtime — by ${TIME_LABEL[timeGranularity]}`}</Header>}>
      <SpaceBetween size="l">
        <div data-testid="program-table-downtime">
        <Table<DowntimeRow>
          trackBy={(item: DowntimeRow) => `${item.program}__${item.cm}__${item.cm_location}`}
          columnDefinitions={[
            { id: "program", header: "Program", width: 150, cell: (item: DowntimeRow) => {
              const rowKey = `${item.program}__${item.cm}__${item.cm_location}`;
              const childCount = (dtChildrenMap.get(rowKey) || []).length;
              if (!item.parent_program && childCount > 0) {
                return (
                  <span className="detail-expandable-program">
                    <span>{item.program}</span>
                    <span className="detail-child-count-badge">{childCount}</span>
                  </span>
                );
              }
              return item.program;
            }},
            { id: "cmLocation", header: "CM & Location", width: 90, cell: (item: DowntimeRow) => item.parent_program ? "" : item.cmLocation },
            { id: "smtTarget", header: "SMT Target", width: 80, cell: (item: DowntimeRow) => item.hasSmt && item.smtTarget != null ? `${(item.smtTarget * 100).toFixed(1)}%` : "—" },
            { id: "smtActual", header: "SMT Actual", width: 80, cell: (item: DowntimeRow) => {
              if (!item.hasSmt || item.smtActual == null) return "—";
              return `${(item.smtActual * 100).toFixed(2)}%`;
            }},
            { id: "fatpTarget", header: "FATP Target", width: 85, cell: (item: DowntimeRow) => item.hasFatp && item.fatpTarget != null ? `${(item.fatpTarget * 100).toFixed(1)}%` : "—" },
            { id: "fatpActual", header: "FATP Actual", width: 85, cell: (item: DowntimeRow) => {
              if (!item.hasFatp || item.fatpActual == null) return "—";
              return `${(item.fatpActual * 100).toFixed(2)}%`;
            }},
          ]}
          items={pageItems}
          expandableRows={{
            getItemChildren: (item: DowntimeRow) => {
              const rowKey = `${item.program}__${item.cm}__${item.cm_location}`;
              return dtChildrenMap.get(rowKey) || [];
            },
            isItemExpandable: (item: DowntimeRow) => {
              const rowKey = `${item.program}__${item.cm}__${item.cm_location}`;
              return (dtChildrenMap.get(rowKey) || []).length > 0;
            },
            expandedItems: expandedDtItems,
            onExpandableItemToggle: ({ detail }) => {
              const item = detail.item;
              const isExpanded = detail.expanded;
              const getKey = (i: DowntimeRow) => `${i.program}__${i.cm}__${i.cm_location}`;
              if (isExpanded) {
                setExpandedDtItems((prev) => [...prev, item]);
              } else {
                setExpandedDtItems((prev) => prev.filter((i) => getKey(i) !== getKey(item)));
              }
            },
          }}
          variant="embedded"
          filter={<TextFilter filteringText={dtSearchText} onChange={({ detail }) => { setDtSearchText(detail.filteringText); setDtPage(1); }} filteringPlaceholder="Search by program, CM or CM location..." />}
          empty={<Box textAlign="center" color="inherit" padding={{ vertical: "l" }}><b>No machine downtime data</b><Box color="text-body-secondary" padding={{ top: "s" }}>No machine downtime records available for the selected period.</Box></Box>}
          pagination={
            <div className="detail-table-toolbar">
              <div className="detail-page-size-selector">
                <span className="detail-page-size-label">Page size:</span>
                <div className="detail-page-size-select">
                  <Select
                    selectedOption={selectedPageSize}
                    onChange={({ detail }) => { setSelectedPageSize(detail.selectedOption as SelectProps.Option); setDtPage(1); }}
                    options={PAGE_SIZE_OPTIONS}
                  />
                </div>
              </div>
              {filteredDtDisplay.length > DT_PAGE_SIZE && (
                <div data-testid="program-table-downtime-pagination">
                  <Pagination currentPageIndex={dtPage} pagesCount={Math.ceil(filteredDtDisplay.length / DT_PAGE_SIZE)} onChange={({ detail }) => setDtPage(detail.currentPageIndex)} />
                </div>
              )}
            </div>
          }
        />
        </div>

        <div className="detail-separator" />

        {/* SMT & FATP Downtime Trend Charts */}
        <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
          <div data-testid="program-chart-smt-downtime">
            <Box variant="h4" textAlign="center" padding={{ bottom: "xs" }}><span data-testid="program-chart-smt-downtime-title">SMT Machine Downtime Trend</span></Box>
            <Plot
              data={smtChartData}
              layout={chartLayout}
              config={chartConfig}
              style={{ width: "100%" }}
            />
          </div>

          <div data-testid="program-chart-fatp-downtime">
            <Box variant="h4" textAlign="center" padding={{ bottom: "xs" }}><span data-testid="program-chart-fatp-downtime-title">FATP Machine Downtime Trend</span></Box>
            <Plot
              data={fatpChartData}
              layout={chartLayout}
              config={chartConfig}
              style={{ width: "100%" }}
            />
          </div>
        </Grid>
      </SpaceBetween>
    </Container>
    </div>
  );
};
